import {
    calcPrinterMonthlyStatsRaw,
    PrinterMonthlyStatsRaw,
} from '../../profile/printer-content/helpers/calc-printer-monthly-stats'
import PrintJobExecutor from '../../../types/api/print-job-executor'
import User, { PrinterMachineColumn, PrinterShift } from '../../../types/api/user'
import { PRINTER_MACHINE_COLUMNS, PRINTER_SHIFTS } from '../constants/shift-grid'
import { isAssistantShiftProfile, isPrinterShiftProfile } from './is-shift-profile'

export interface VolumeSummaryCell {
    documentId: string
    username: string
    stats: PrinterMonthlyStatsRaw
}

export interface VolumeSummaryShift {
    shift: PrinterShift
    cells: Partial<Record<PrinterMachineColumn, VolumeSummaryCell[]>>
    assistants: VolumeSummaryCell[]
    volume: number | null
}

export interface VolumeSummaryView {
    shifts: VolumeSummaryShift[]
    totalVolume: number | null
}

function groupExecutorsByUser(
    executors: PrintJobExecutor[]
): Map<string, PrintJobExecutor[]> {
    const grouped = new Map<string, PrintJobExecutor[]>()

    for (const executor of executors) {
        const userId = executor.user?.documentId

        if (!userId) {
            continue
        }

        const current = grouped.get(userId) ?? []
        current.push(executor)
        grouped.set(userId, current)
    }

    return grouped
}

function statsForUser(
    user: User,
    executorsByUser: Map<string, PrintJobExecutor[]>,
    machineColumn?: PrinterMachineColumn
): VolumeSummaryCell {
    const executors = executorsByUser.get(user.documentId) ?? []
    const forMachine = machineColumn
        ? executors.filter(executor => executor.machine?.name === machineColumn)
        : executors

    return {
        documentId: user.documentId,
        username: user.username,
        stats: calcPrinterMonthlyStatsRaw(forMachine),
    }
}

function sumVolumes(values: Array<number | null>): number | null {
    let sum = 0
    let hasVolume = false

    for (const value of values) {
        if (value == null) {
            continue
        }

        sum += value
        hasVolume = true
    }

    return hasVolume ? sum : null
}

export function buildVolumeSummary(
    users: User[],
    executors: PrintJobExecutor[]
): VolumeSummaryView {
    const executorsByUser = groupExecutorsByUser(executors)
    const totalVolume = calcPrinterMonthlyStatsRaw(executors).volume

    const shifts = PRINTER_SHIFTS.map(shift => {
        const cells: Partial<Record<PrinterMachineColumn, VolumeSummaryCell[]>> = {}
        const assistants: VolumeSummaryCell[] = []

        for (const user of users) {
            if (isPrinterShiftProfile(user.shiftProfile)) {
                if (user.shiftProfile.data.shift !== shift) {
                    continue
                }

                const column = user.shiftProfile.data.machineColumn
                const current = cells[column] ?? []
                current.push(statsForUser(user, executorsByUser, column))
                cells[column] = current
                continue
            }

            if (
                isAssistantShiftProfile(user.shiftProfile) &&
                user.shiftProfile.data.shift === shift
            ) {
                assistants.push(statsForUser(user, executorsByUser))
            }
        }

        assistants.sort((left, right) => left.username.localeCompare(right.username, 'ru'))

        for (const column of PRINTER_MACHINE_COLUMNS) {
            cells[column]?.sort((left, right) => left.username.localeCompare(right.username, 'ru'))
        }

        const volumes = [
            ...PRINTER_MACHINE_COLUMNS.flatMap(column => cells[column] ?? []),
            ...assistants,
        ].map(cell => cell.stats.volume)

        return { shift, cells, assistants, volume: sumVolumes(volumes) }
    })

    return { shifts, totalVolume }
}
